import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { env } from "@/lib/env";
import { ResultStage, formatExpiry } from "@/components/shared/InviteResultStage";
import type { ProsthesisLabRow } from "./types";

type Props = {
  lab: ProsthesisLabRow | null;
  onClose: () => void;
};

/** Gera e mostra o link pra verificar um ProsthesisLab contra a conta real do laboratório. */
export function ProsthesisLabConnectDialog({ lab, onClose }: Props) {
  const open = !!lab;
  const utils = trpc.useUtils();
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const createInvite = trpc.prosthesis.lab.connectInvite.create.useMutation({
    onSuccess: (invite) => {
      setToken(invite.token);
      setExpiresAt(invite.expiresAt ? new Date(invite.expiresAt) : null);
    },
    onError: (err) => toast.error(err.message || "Erro ao gerar convite."),
  });

  useEffect(() => {
    if (lab) createInvite.mutate({ prosthesisLabId: lab.id });
    else {
      setToken(null);
      setExpiresAt(null);
      setCopied(false);
      setShowQr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab?.id]);

  const handleClose = () => {
    onClose();
    utils.prosthesis.lab.list.invalidate();
  };

  const inviteUrl = token ? `${env.landingUrl}/conectar-laboratorio/${token}` : "";
  const phone = lab?.phone ?? null;
  const email = lab?.email ?? null;

  const toWaNumber = (v: string) => {
    const d = v.replace(/\D/g, "");
    return d.startsWith("55") ? d : `55${d}`;
  };
  const whatsappText = `Olá! Conecte o laboratório de vocês ao DentalBook pra receber os pedidos direto na conta: ${inviteUrl}`;
  const whatsappHref = phone
    ? `https://wa.me/${toWaNumber(phone)}?text=${encodeURIComponent(whatsappText)}`
    : `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const emailSubject = "Conectar laboratório no DentalBook";
  const emailBody = `Olá!%0D%0A%0D%0APra receber os pedidos de prótese direto na conta de vocês, acesse:%0D%0A${encodeURIComponent(inviteUrl)}%0D%0A%0D%0AObrigado!`;
  const mailtoHref = email
    ? `mailto:${email}?subject=${encodeURIComponent(emailSubject)}&body=${emailBody}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const expiryLabel = expiresAt ? formatExpiry(expiresAt) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle className="dark:text-foreground" ajuda={AJUDA_PROTESE.conectarLaboratorio}>
            Conectar laboratório
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            Envie esse link pra {lab?.name} — ao aceitar (logado na conta real do laboratório), os
            pedidos enviados a esse contato passam a aparecer direto na conta deles.
          </DialogDescription>
        </DialogHeader>

        {!token ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : (
          <ResultStage
            inviteUrl={inviteUrl}
            phone={phone}
            email={email}
            whatsappHref={whatsappHref}
            mailtoHref={mailtoHref}
            copied={copied}
            onCopy={copy}
            showQr={showQr}
            onToggleQr={() => setShowQr((v) => !v)}
            expiryLabel={expiryLabel}
            onNewLink={() => lab && createInvite.mutate({ prosthesisLabId: lab.id })}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
